import { Divider, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TCreateConnectionConfigurationFormProps } from '@/console/features/devices/components/create-device/create-connection-configuration-form.types';
import { TConnectionConfigurationInputSchema } from '@/console/features/devices/components/create-device/create-device.types';
import { CreateFtpConfigurationForm } from '@/console/features/devices/components/create-ftp-configuration/create-ftp-configuration-form';
import { CreateSmbConfigurationForm } from '@/console/features/devices/components/create-smb-configuration/create-smb-configuration-form';
import { GatewaySelect } from '@/console/features/gateways/components/gateway-select/gateway-select';
import { Alert } from '@/shared/components/ui/alert/alert';
import { FormInput } from '@/shared/components/ui/form/components/form-input/form-input';
import { IpAddressFormInput } from '@/shared/components/ui/form/components/form-input/variant/ip-address-form-input/ip-address-form-input';
import { RadioToggle } from '@/shared/components/ui/form/components/radio-toggle/radio-toggle';
import { Tab } from '@/shared/components/ui/tabs/components/tab/tab';
import { Tabs } from '@/shared/components/ui/tabs/tabs';
import { SmbProtocolEnum } from '@/shared/types/types.generated';

type ProtocolType = 'ftp' | 'smb';

export const CreateConnectionConfigurationForm: React.FC<
  TCreateConnectionConfigurationFormProps
> = ({ boundIpAddress }) => {
  const { t } = useTranslation();
  const { control, setValue } =
    useFormContext<TConnectionConfigurationInputSchema>();
  const [activeTab, setActiveTab] = useState<ProtocolType>('ftp');
  const [enableFtp, enableSmb] = useWatch({
    control,
    name: ['enableFtp', 'enableSmb'],
  });

  // Not a default value: stepping back keeps this step's old values, which would win.
  useEffect(() => {
    if (boundIpAddress) {
      setValue('connectionConfiguration.ipAddress', boundIpAddress, {
        shouldValidate: true,
      });
    }
  }, [boundIpAddress, setValue]);

  const handleTabChange = (
    _event: React.SyntheticEvent,
    newValue: ProtocolType,
  ) => {
    setActiveTab(newValue);
    setValue('smbConfiguration.protocol', SmbProtocolEnum.Smb);
    setValue('smbConfiguration.port', 445);
  };

  return (
    <>
      <IpAddressFormInput<TConnectionConfigurationInputSchema>
        name="connectionConfiguration.ipAddress"
        label={t('device:ip_address_plc')}
        required
        disabled={Boolean(boundIpAddress)}
      />
      {boundIpAddress && (
        <Alert
          color="blue"
          text={t('openplc:provisioning.binding.ip_locked')}
        />
      )}
      <FormInput<TConnectionConfigurationInputSchema>
        name="connectionConfiguration.port"
        label={t('device:port')}
        required
        type="number"
      />
      <GatewaySelect<TConnectionConfigurationInputSchema>
        name="gatewayId"
        getItemId={(value) => value}
        multiple={false}
      />

      <Divider />
      <Typography variant="p2">{t('device:protocol_configuration')}</Typography>
      <Tabs value={activeTab} onChange={handleTabChange}>
        <Tab label={t('device:ftp_configuration')} value="ftp" />
        <Tab label={t('device:smb_configuration')} value="smb" />
      </Tabs>
      {activeTab === 'ftp' && (
        <Stack gap="1rem">
          <RadioToggle<TConnectionConfigurationInputSchema>
            name="enableFtp"
            label={t('device:enable_ftp')}
          />
          <CreateFtpConfigurationForm disabled={!enableFtp} />
        </Stack>
      )}

      {activeTab === 'smb' && (
        <Stack gap="1rem">
          <RadioToggle<TConnectionConfigurationInputSchema>
            name="enableSmb"
            label={t('device:enable_smb')}
          />
          <CreateSmbConfigurationForm disabled={!enableSmb} />
        </Stack>
      )}
    </>
  );
};
